/**
 * OrgDesignView - Displays organizational design recommendations
 * Reads from orgReport.analysis.org_design_recommendations
 */
import React from 'react';
import {
    Users, ArrowRight, Loader2, Settings, Briefcase,
    Star, CheckCircle2, AlertTriangle, Target, RefreshCw
} from 'lucide-react';

const CHANGE_TYPE_COLORS = {
    consolidate: '#5856D6',
    split: '#FF9500',
    create: '#34C759',
    eliminate: '#FF3B30',
    move: '#007AFF',
    realign: '#FF6B35',
    outsource: '#86868B',
};

function OrgDesignView({ orgReport, loading, onRegenerate }) {
    if (loading) {
        return (
            <div className="rp-loading">
                <Loader2 className="rp-spinner-icon" size={24} />
                <p>Loading org design recommendations...</p>
            </div>
        );
    }

    const orgDesign = orgReport?.analysis?.org_design_recommendations;

    // Check if org report exists but is missing new fields (stale data)
    const hasReport = !!orgReport?.analysis;

    if (!orgDesign) {
        return (
            <div className="rp-empty-state">
                <div className="rp-empty-icon"><Users size={32} /></div>
                <h3>{hasReport ? 'Report Needs Regeneration' : 'No Organization Design Recommendations'}</h3>
                <p>{hasReport 
                    ? 'This report was generated before org design analysis was available. Regenerate to unlock this feature.'
                    : 'Generate an organization report first to see structural optimization recommendations.'}
                </p>
                {onRegenerate && (
                    <button className="rp-btn rp-btn-primary" onClick={onRegenerate} style={{ marginTop: '1rem' }}>
                        <RefreshCw size={14} /> {hasReport ? 'Regenerate Org Report' : 'Generate Org Report'}
                    </button>
                )}
            </div>
        );
    }

    return (
        <div className="org-design-view">
            {/* Summary */}
            {orgDesign.org_design_summary && (
                <div className="ds-section" style={{ marginBottom: '1.5rem' }}>
                    <div className="ds-section-header">
                        <h2><Users size={18} style={{ marginRight: 8 }} /> Organization Design Analysis</h2>
                    </div>
                    <div className="ds-section-body">
                        <p className="retention-summary">{orgDesign.org_design_summary}</p>
                    </div>
                </div>
            )}

            {/* Structural Observations */}
            {(orgDesign.structural_observations || []).length > 0 && (
                <div className="ds-section" style={{ marginBottom: '1.5rem' }}>
                    <div className="ds-section-header">
                        <h2><AlertTriangle size={18} style={{ marginRight: 8 }} /> Structural Observations</h2>
                    </div>
                    <div className="ds-section-body">
                        <div className="org-observations">
                            {orgDesign.structural_observations.map((obs, i) => (
                                <div key={i} className="org-observation-card">
                                    <h4>{obs.observation}</h4>
                                    <p><strong>Departments:</strong> {(obs.departments_involved || []).join(', ')}</p>
                                    <p><strong>Evidence:</strong> {obs.evidence}</p>
                                    <p><strong>Impact:</strong> {obs.impact}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Recommended Structural Changes */}
            {(orgDesign.recommended_structural_changes || []).length > 0 && (
                <div className="ds-section" style={{ marginBottom: '1.5rem' }}>
                    <div className="ds-section-header">
                        <h2><Settings size={18} style={{ marginRight: 8 }} /> Recommended Changes</h2>
                    </div>
                    <div className="ds-section-body">
                        <div className="org-changes">
                            {orgDesign.recommended_structural_changes.map((change, i) => {
                                const color = CHANGE_TYPE_COLORS[change.change_type] || '#86868B';
                                return (
                                    <div key={i} className="org-change-card" style={{ borderLeftColor: color }}>
                                        <div className="org-change-header">
                                            <span className="org-change-type" style={{ backgroundColor: color }}>
                                                {change.change_type}
                                            </span>
                                            <span className="org-change-complexity">
                                                {change.implementation_complexity} complexity
                                            </span>
                                        </div>
                                        <h4>{change.description}</h4>
                                        <p><strong>Departments:</strong> {(change.departments_affected || []).join(', ')}</p>
                                        <p><strong>Rationale:</strong> {change.rationale}</p>
                                        <div className="org-change-meta">
                                            <span className="org-headcount-impact">
                                                <Users size={12} /> Headcount: {(change.headcount_impact || 'neutral').replace(/_/g, ' ')}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {/* Collaboration Improvements */}
            {(orgDesign.collaboration_improvements || []).length > 0 && (
                <div className="ds-section" style={{ marginBottom: '1.5rem' }}>
                    <div className="ds-section-header">
                        <h2><Briefcase size={18} style={{ marginRight: 8 }} /> Collaboration Improvements</h2>
                    </div>
                    <div className="ds-section-body">
                        <div className="org-collabs">
                            {orgDesign.collaboration_improvements.map((collab, i) => (
                                <div key={i} className="org-collab-card">
                                    <h4>{collab.improvement}</h4>
                                    <p><strong>Departments:</strong> {(collab.departments || []).join(', ')}</p>
                                    <p><strong>Expected Benefit:</strong> {collab.expected_benefit}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* New Capabilities Needed */}
            {(orgDesign.new_capabilities_needed || []).length > 0 && (
                <div className="ds-section">
                    <div className="ds-section-header">
                        <h2><Star size={18} style={{ marginRight: 8 }} /> New Capabilities Needed</h2>
                    </div>
                    <div className="ds-section-body">
                        <ul className="retention-drivers-list">
                            {orgDesign.new_capabilities_needed.map((cap, i) => (
                                <li key={i}><Target size={14} style={{ color: '#007AFF' }} /> {cap}</li>
                            ))}
                        </ul>
                    </div>
                </div>
            )}
        </div>
    );
}

export default OrgDesignView;
